import { useCallback, useContext, useMemo } from 'react';
import { findFormatter } from '../../../../../../../../Utils/Formatters/FindFormatter';
import { useBusinessDataFormContext } from '../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import { IFormData } from '../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { FormUpdateAssociatonType } from '../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import KonteringContext from '../../../../../../_Common/Components/KonteringManager/KonteringContext';
import { isVisibleByKontodelDate } from '../../../../../../_Common/Components/KonteringManager/validateKonteringForAttestUtils';
import BokforderContext from '../../BokforderContext/BokforderContext';
import {
  IInternalSelectableColumnHeader,
  ISelectableColumnHeader,
} from '../../ImportDialog/ImportDialog';
import { isRowEmpty } from '../../../Utils/BokforderUtils';
import { BokforderType__Associations_Rader } from '../../../Queries/_QueryTypes/BokforderType';
import {
  currencyValueFormatter,
  isNumberCellValueValid,
} from '../../ImportDialog/Utils/importDialogUtils';

const currFormatter = findFormatter('TEDFormatCurr');

const hasNonZeroNumericValue = (value?: string): boolean => {
  if (value == undefined) {
    return false;
  }

  const parsedValue = parseFloat(value);
  return parsedValue !== 0 && !isNaN(parsedValue);
};

const getRowValuesForImport = (rowCollectionData: IFormData): IFormData => {
  const textValue =
    rowCollectionData?.TEXT != undefined
      ? { TEXT: rowCollectionData.TEXT }
      : {};
  const hasSaldoValue = hasNonZeroNumericValue(rowCollectionData?.Saldo);

  if (hasSaldoValue) {
    const parsedSaldoValue =
      currFormatter.parser
        .parse(rowCollectionData?.Saldo ?? '0')
        .value?.toString() ?? '0';
    const saldoAmount = parseFloat(parsedSaldoValue);
    const isDebit = saldoAmount > 0;

    return {
      ...(isDebit && {
        DEBET: saldoAmount,
      }),
      ...(!isDebit && {
        KREDIT: Math.abs(saldoAmount),
      }),
      ...textValue,
    };
  }

  return {
    ...(hasNonZeroNumericValue(rowCollectionData?.DEBET) && {
      DEBET: currFormatter.parser.parse(rowCollectionData?.DEBET ?? '').value,
    }),
    ...(hasNonZeroNumericValue(rowCollectionData?.KREDIT) && {
      KREDIT: currFormatter.parser.parse(rowCollectionData?.KREDIT ?? '').value,
    }),
    ...textValue,
  };
};

export const useBokforderImportFromClipboardSelectableColumnHeaders = (
  includeKreditColumn?: boolean
): ISelectableColumnHeader[] => {
  const bokforderContext = useContext(BokforderContext);
  const konteringContext = useContext(KonteringContext);
  const getKontodelByKontodel =
    konteringContext.konteringUtils.getKontodelByKontodel;

  const isKontodelValid = useCallback(
    (
      cellValue: string,
      row: any,
      columnInfo: IInternalSelectableColumnHeader
    ) => {
      const kontodel = getKontodelByKontodel(
        bokforderContext.bokforderKodplan?.id ?? '',
        parseInt(columnInfo.associationName?.split('Kontodel')?.[1] ?? '') ?? 0,
        cellValue,
        bokforderContext.bokforderDate ?? new Date()
      );
      return {
        validValue: kontodel != null ? true : false,
        errorMessage:
          kontodel != null
            ? ''
            : columnInfo.label + ' "' + cellValue + '" kunde inte hittas',
        errorColumn:
          kontodel != null
            ? ''
            : columnInfo.associationName + '.' + columnInfo.attribute,
      };
    },
    [
      bokforderContext.bokforderDate,
      bokforderContext.bokforderKodplan?.id,
      getKontodelByKontodel,
    ]
  );

  const selectableColumnHeaders: ISelectableColumnHeader[] = useMemo(() => {
    const columns: ISelectableColumnHeader[] = [
      {
        id: 'kodsträng',
        attribute: 'Kodsträng',
        customLabel: 'Kodsträng',
      },
      {
        id: 'kontodel1',
        associationName: 'Kontodel1',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'Kontodel2',
        associationName: 'Kontodel2',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'Kontodel3',
        associationName: 'Kontodel3',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'Kontodel4',
        associationName: 'Kontodel4',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'Kontodel5',
        associationName: 'Kontodel5',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'Kontodel6',
        associationName: 'Kontodel6',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'debet',
        attribute: 'DEBET',
        customColumnOptions: {
          valueFormatter: currencyValueFormatter,
        },
        isCellValueValid: isNumberCellValueValid,
      },
      ...(includeKreditColumn
        ? ([
            {
              id: 'kredit',
              attribute: 'KREDIT',
              customColumnOptions: {
                valueFormatter: currencyValueFormatter,
              },
              isCellValueValid: isNumberCellValueValid,
            },
            {
              id: 'saldo',
              attribute: 'Saldo',
              customLabel: 'Saldo',
              customColumnOptions: {
                valueFormatter: currencyValueFormatter,
              },
              isCellValueValid: isNumberCellValueValid,
            },
          ] as ISelectableColumnHeader[])
        : []),
      {
        id: 'radtext',
        attribute: 'TEXT',
      },
    ];

    return columns;
  }, [isKontodelValid, includeKreditColumn]);

  return selectableColumnHeaders;
};

export const useBokforderImportFromClipboardOnCreateCallback = () => {
  const bokforderContext = useContext(BokforderContext);
  const makeNewRow = bokforderContext.makeNewRow;
  const updateRow = bokforderContext.updateRow;
  const formContext = useBusinessDataFormContext();
  const formContextUpdate = formContext?.update;
  const konteringContext = useContext(KonteringContext);
  const getKontodelByKontodel =
    konteringContext.konteringUtils.getKontodelByKontodel;

  return useCallback(
    (
      chosenRows: {
        id: string;
        collectionData: IFormData;
      }[]
    ) => {
      const newValues: FormUpdateAssociatonType[][] = [];
      const today = new Date();
      const usedEmptyRows: string[] = [];

      chosenRows.forEach(row => {
        const rowCollectionData = row.collectionData;

        //Tar fram rader som inte är en mallrad och saknar koddelar
        const emptyNonTemplateRows: BokforderType__Associations_Rader[] =
          formContext?.formState.currentValues?._Associations?.Rader?.filter(
            (row: BokforderType__Associations_Rader) =>
              isRowEmpty(row) && row.MALLRAD_LOPNR == null
          );

        const firstEmptyRow = emptyNonTemplateRows.find(
          emptyRow => !usedEmptyRows.includes(emptyRow.id)
        );

        const newRowID =
          firstEmptyRow != undefined
            ? firstEmptyRow.id
            : NEWLY_ADDED_ID_PREFIX_FOR_UPDATE + row.id;

        if (firstEmptyRow != undefined) {
          usedEmptyRows.push(firstEmptyRow.id);
        }

        const isNewRow = newRowID.includes(NEWLY_ADDED_ID_PREFIX_FOR_UPDATE);
        const rowValues = getRowValuesForImport(rowCollectionData);

        if (isNewRow) {
          newValues.push(makeNewRow(newRowID, rowValues));
        } else {
          newValues.push(updateRow(newRowID, rowValues));
        }

        if (rowCollectionData?._Associations) {
          for (let association in rowCollectionData._Associations) {
            if (association.includes('Kontodel')) {
              const newKontodel =
                rowCollectionData?._Associations?.[`${association}`];

              const actualKontodel = getKontodelByKontodel?.(
                bokforderContext.bokforderKodplan?.id ?? '',
                parseInt(association?.split('Kontodel')?.[1]) ?? -1,
                newKontodel?.KONTODEL ?? '',
                today
              );

              if (actualKontodel) {
                const visibleByDate = isVisibleByKontodelDate(
                  actualKontodel,
                  today
                );

                if (visibleByDate && actualKontodel.aktiv) {
                  newValues.push([
                    {
                      associationName: 'Rader',
                      operation: isNewRow ? 'Add_Values' : 'Update_Values',
                      id: newRowID,
                    },
                    {
                      associationName: `${association}`,
                      operation: 'Ref',
                      id: actualKontodel?.id ?? null,
                      values: actualKontodel?.id
                        ? { id: actualKontodel?.id }
                        : null,
                    },
                  ]);
                }
              }
            }
          }
        }
      });

      formContextUpdate?.(newValues);
    },
    [
      formContextUpdate,
      formContext?.formState.currentValues?._Associations?.Rader,
      makeNewRow,
      updateRow,
      getKontodelByKontodel,
      bokforderContext.bokforderKodplan?.id,
    ]
  );
};
